import { GENERAL_TEXT } from '@/texts/catalog/general';

export const DEFAULT_CURRENCY = 'MXN';

export const normalizeCurrencyCode = (currency, fallback = DEFAULT_CURRENCY) => {
  const currencyCode = String(currency || '').trim().toUpperCase();
  const normalizedFallback = String(fallback || '').trim().toUpperCase();
  return currencyCode === '' ? normalizedFallback : currencyCode;
};

export const formatInteger = (value, locale = 'es-MX') => {
  const number = Number(value || 0);

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(Number.isFinite(number) ? number : 0);
};

export const formatCurrency = (
  value,
  {
    locale = 'es-MX',
    currency = DEFAULT_CURRENCY,
    maximumFractionDigits = 2,
  } = {}
) => {
  const number = Number(value || 0);
  const currencyCode = normalizeCurrencyCode(currency, DEFAULT_CURRENCY);

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits,
  }).format(Number.isFinite(number) ? number : 0);
};

export const formatCurrencyDisplay = (
  value,
  {
    locale = 'es-MX',
    currency = DEFAULT_CURRENCY,
    maximumFractionDigits = 2,
  } = {}
) => {
  return formatCurrency(value, {
    locale,
    currency,
    maximumFractionDigits,
  })
    .replace(/^\$/, '$ ')
    .replace(/^-\$/, '-$ ');
};

export const isForeignCurrency = (currency) => {
  const currencyCode = normalizeCurrencyCode(currency, '');
  return currencyCode !== '' && currencyCode !== DEFAULT_CURRENCY;
};

export const formatAmountWithCurrency = (value, currency) => {
  const amount = formatCurrency(value, { currency: DEFAULT_CURRENCY });
  const currencyCode = normalizeCurrencyCode(currency, '');

  if (currencyCode === '' || currencyCode === DEFAULT_CURRENCY) {
    return amount;
  }

  return `${amount} ${currencyCode}`;
};

export const getRequiredExchangeRate = (row) => {
  const exchangeRate = Number(row?.exchange_rate);
  if (!Number.isFinite(exchangeRate) || exchangeRate <= 0) {
    throw new Error(GENERAL_TEXT.errors.exchangeRateRequired);
  }

  return exchangeRate;
};

export const formatExchangeRate = (
  value,
  {
    locale = 'es-MX',
    minimumFractionDigits = 2,
    maximumFractionDigits = 6,
    invalidValue = '',
  } = {}
) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) {
    return invalidValue;
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(number);
};

export const formatCompactNumber = (
  value,
  {
    millionFractionDigits = 1,
    thousandFractionDigits = 0,
  } = {}
) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return '0';
  }

  const absolute = Math.abs(numeric);

  if (absolute >= 1000000) {
    return `${(numeric / 1000000).toFixed(millionFractionDigits)}M`;
  }

  if (absolute >= 1000) {
    return `${(numeric / 1000).toFixed(thousandFractionDigits)}k`;
  }

  return `${Math.round(numeric)}`;
};
